import { Injectable } from '@angular/core';
import { forkJoin, Observable, of, from } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { BuilderService } from 'src/app/builder-services/builder.service';
import { OrdersApiService } from 'src/app/builder-services/api/orders.api.service'
import { SettingsApiService } from 'src/app/builder-services/api/settings.api.service'

export interface Order {
  order_id: string;
  date?: string;            // "DD-MM-YYYY"
  total?: number;
  subTotal?: number;
  shippingPrice?: number;
  project_name?: string;
  [key: string]: any;
}

export interface OrdersSummaryRow {
  project_id: string;
  project_name: string;
  total_orders: number;
  total_value: number;
  currency: string;
}

@Injectable({ providedIn: 'root' })
export class OrdersStatsService {
  lastIndex: number = 0;

  constructor(
    private builderService: BuilderService,
    private ordersApi: OrdersApiService,
    private settingsApi: SettingsApiService
  ) {}

  /**
   * Return per-project summaries; if start/end are provided, only include orders in that range (inclusive).
   */
  getProjectSummaries(projectIds: string[], start?: Date | null, end?: Date | null): Observable<OrdersSummaryRow[]> {
    const perProjectRequests = projectIds.map(projectId =>
      forkJoin({
        orders: this.getOrders(projectId).pipe(
          catchError(err => { console.warn('getOrders failed for', projectId, err); return of([] as Order[]); })
        ),
        currency: this.getCurrency(projectId),
        projectName: this.getProjectName(projectId)
      })
    );

    return forkJoin(perProjectRequests).pipe(
      map((results) => {
        return projectIds.map((projectId, idx) => {
          const ordersAll = results[idx].orders || [];
          const ordersFiltered = this.filterOrdersByDateRange(ordersAll, start, end);

          const total_orders = ordersFiltered.length;
          const total_value = ordersFiltered.reduce((sum, o) => {
            const val = this.toNum(o?.total) ??
                        (this.toNum(o?.subTotal) ?? 0) + (this.toNum(o?.shippingPrice) ?? 0);
            return sum + val;
          }, 0);

          return {
            project_id: projectId,
            project_name: results[idx].projectName,
            currency: results[idx].currency,
            total_orders,
            total_value
          };
        });
      })
    );
  }

  /**
   * Get ALL orders for a project from Supabase, sorted by date ascending.
   */
  getOrders(project_id: string): Observable<Order[]> {
    return from(
      this.ordersApi.getOrders(project_id).then((orders) =>
        orders
          .map((o: any) => ({
            order_id: o.order_id,
            date: o.date ? this.isoToddmmyyyy(o.date) : undefined,
            total: o.total,
            subTotal: o.subTotal,
            shippingPrice: o.shippingPrice,
            user: o.user,
            user_email: o.user_email,
            user_data: o.user_data,
          }))
          .sort((a: Order, b: Order) => {
            const dateA = this.parseDate(a.date).valueOf()
            const dateB = this.parseDate(b.date).valueOf()
            return dateA - dateB
          })
      )
    );
  }

  /** ----------- low-level fetches & helpers ----------- */

  private getCurrency(projectId: string): Observable<string> {
    return from(
      this.settingsApi.getSettingsByType(projectId, 'general_settings')
        .then((s: any) => s?.currency ? String(s.currency) : 'EGP')
        .catch(() => 'EGP')
    );
  }

  private getProjectName(projectId: string): Observable<string> {
    return from(
      this.settingsApi.getSettingsByType(projectId, 'general_settings')
        .then((s: any) => s?.application_name ? String(s.application_name) : '')
        .catch(() => '')
    );
  }

  private filterOrdersByDateRange(orders: Order[], start?: Date | null, end?: Date | null): Order[] {
    if (!start && !end) return orders;

    const startTime = start ? this.stripTime(start).getTime() : -Infinity;
    const endTime   = end   ? this.stripTime(end).getTime()   : +Infinity;

    return orders.filter(o => {
      const d = this.parseDate(o.date);
      const t = d.getTime();
      return t >= startTime && t <= endTime;
    });
  }

  private isoToddmmyyyy(iso: string): string {
    const d = new Date(iso)
    const day = String(d.getDate()).padStart(2, '0')
    const month = String(d.getMonth() + 1).padStart(2, '0')
    const year = d.getFullYear()
    return `${day}-${month}-${year}`
  }

  private stripTime(d: Date): Date {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }

  private parseDate(dateString?: string): Date {
    if (!dateString) return new Date(0);
    const [day, month, year] = dateString.split('-'); // "DD-MM-YYYY"
    return new Date(Number(year), Number(month) - 1, Number(day));
  }

  private toNum(v: any): number | undefined {
    if (v === null || v === undefined) return undefined;
    const n = Number(v);
    return Number.isFinite(n) ? n : undefined;
  }
}
